import React, { useState, useEffect, useRef } from 'react';
import { Tree } from 'primereact/tree';
import { Dropdown } from 'primereact/dropdown';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import 'primereact/resources/themes/lara-light-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import './TreeViewSidebar.css';
import apiRoutes from '../api/apiRoutes';
import { apiGet } from '../api/request';
import GavelRoundedIcon from '@mui/icons-material/GavelRounded';
import AccountTreeRoundedIcon from '@mui/icons-material/AccountTreeRounded';
import BalanceRoundedIcon from '@mui/icons-material/BalanceRounded';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import ViewDayRoundedIcon from '@mui/icons-material/ViewDayRounded';
import EmojiObjectsRoundedIcon from '@mui/icons-material/EmojiObjectsRounded';
import ViewModuleRoundedIcon from '@mui/icons-material/ViewModuleRounded';
import DocumentScannerRoundedIcon from '@mui/icons-material/DocumentScannerRounded';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import EditNoteRoundedIcon from '@mui/icons-material/EditNoteRounded';

export default function TreeViewSidebar({ ebookContentData, onNodeSelect, onAddNode, onDragDropUpdate, onDeleteNode }) {
  const [treeNodes, setTreeNodes] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [dropdownOpenKey, setDropdownOpenKey] = useState(null);
  const [draggingNodeId, setDraggingNodeId] = useState(null);
  const [genreOptions, setGenreOptions] = useState([]);
  const [showGenreDropdown, setShowGenreDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const childDropdownRef = useRef(null);

  useEffect(() => {
    const fetchGenres = async () => {
      try {
        const genreRes = await apiGet(apiRoutes.getGenres);
        if (Array.isArray(genreRes.responsedata)) {
          setGenreOptions(
            genreRes.responsedata.map((genre) => ({
              label: genre.name,
              value: genre.id,
            }))
          );
        }
      } catch (err) {
        console.error("Error fetching genres:", err.message);
      }
    };

    fetchGenres();
  }, []);

  useEffect(() => {
    const nodes = transformToTreeNodes(ebookContentData);
    setTreeNodes(nodes);
  }, [ebookContentData]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isDropdown = event.target.closest('.p-dropdown');
      const isDropdownPanel = event.target.closest('.p-dropdown-panel');
      if (isDropdown || isDropdownPanel) {
        return;
      }

      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowGenreDropdown(false);
      }

      if (childDropdownRef.current && !childDropdownRef.current.contains(event.target)) {
        setDropdownOpenKey(null);
      }
    };

    if (showGenreDropdown || dropdownOpenKey) {
      const timer = setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 100);

      return () => {
        clearTimeout(timer);
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [showGenreDropdown, dropdownOpenKey]);

  const handleSelection = (e) => {
    const selectedItem = e.node?.data;
    if (selectedItem) {
      setSelectedId(selectedItem.id);
      onNodeSelect(selectedItem);
    }
  };

  const addChildNode = (nodes, nodeKey, genreId) => {
    const genre = genreOptions.find((option) => option.value === genreId);
    const genreName = genre ? genre.label : 'Unknown Genre';
    const genreIdValue = genre ? genre.value : null;
    return nodes.map(node => {
      if (node.key === nodeKey) {
        const timestamp = Date.now();
        const newChildData = {
          id: timestamp,
          title: genreName,
          header: "",
          draft_content: ".",
          verified_content: "",
          is_verified: false,
          order: (node.children?.length || 0) + 1,
          genre_id: genreIdValue,
          genre: {
            id: genreIdValue,
            name: genreName,
          },
          cover_id: null,
          parent_id: node.data.id,
          created_at: new Date().toISOString(),
          created_by: 1,
          created_on: new Date().toISOString().split('T')[0],
          children: [],
        };
        const newChild = {
          key: timestamp.toString(),
          label: genreName,
          data: newChildData,
          icon: getIconByGenre(genreName),
          children: [],
          leaf: true,
        };
        if (onAddNode) {
          onAddNode(newChildData);
        }
        return {
          ...node,
          children: [...(node.children || []), newChild],
          leaf: false,
        };
      } else if (node.children) {
        return {
          ...node,
          children: addChildNode(node.children, nodeKey, genreId),
        };
      }
      return node;
    });
  };

  const handleAddGenre = (genreId, parentKey) => {
    const updated = addChildNode(treeNodes, parentKey, genreId);
    setTreeNodes(updated);
    setDropdownOpenKey(null);
  };

  const handleAddParentNode = (genreId) => {
    const genre = genreOptions.find((option) => option.value === genreId);
    const genreName = genre ? genre.label : 'Unknown Genre';
    const genreIdValue = genre ? genre.value : null;
    const timestamp = Date.now();
    const newParentData = {
      id: timestamp,
      title: genreName,
      header: "",
      draft_content: ".",
      verified_content: "",
      is_verified: false,
      order: treeNodes.length + 1,
      genre_id: genreIdValue,
      genre: {
        id: genreIdValue,
        name: genreName,
      },
      cover_id: null,
      parent_id: null,
      created_at: new Date().toISOString(),
      created_by: 1,
      created_on: new Date().toISOString().split('T')[0],
      children: [],
    };
    const newParent = {
      key: timestamp.toString(),
      label: genreName,
      data: newParentData,
      icon: getIconByGenre(genreName),
      children: [],
      leaf: true,
    };
    setTreeNodes([...treeNodes, newParent]);
    setShowGenreDropdown(false);
    if (onAddNode) {
      onAddNode(newParentData);
      onNodeSelect(newParentData);
    }
  };

  const onDragDrop = (e) => {
    const updatedTreeNodes = e.value;
    const draggedNodeId = e.dragNode?.data?.id;
    setTreeNodes(updatedTreeNodes);
    if (onDragDropUpdate) {
      onDragDropUpdate(updatedTreeNodes, draggedNodeId);
    }
  };

  const handleDeleteNode = (nodeId) => {
    confirmDialog({
      message: 'Are you sure you want to delete this node and all its children?',
      header: 'Confirm Deletion',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        if (onDeleteNode) {
          onDeleteNode(nodeId);
        }
      },
      reject: () => {
        console.log('Deletion cancelled for node ID:', nodeId);
      },
    });
  };

  const nodeTemplate = (node) => {
    const { data, leaf } = node;
    const isSelected = selectedId?.toString() === data.id?.toString();

    return (
      <div
        className={`p-treenode-content flex items-center px-2 py-1 rounded-md cursor-pointer group
          ${isSelected ? 'border-[#1c2526]' : ''} ${leaf ? 'leaf-node' : ''}`}
      >
        <div className="flex items-center gap-1 text-xs text-white truncate">
          <span className="truncate" title={data.title}>{data.title}</span>
        </div>

        <div className="relative ml-auto flex items-center gap-2" ref={childDropdownRef}>
          {dropdownOpenKey === node.key ? (
            <Dropdown
              options={genreOptions}
              onChange={(e) => handleAddGenre(e.value, node.key)}
              placeholder="Select Genre"
              className="text-xs w-32"
              panelClassName="text-xs"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
          ) : (
            <>
              <button
                className="text-gray-400 hover:text-white text-xs invisible group-hover:visible"
                onClick={(e) => {
                  e.stopPropagation();
                  setDropdownOpenKey(node.key);
                }}
                title="Add child"
              >
                <i className="pi pi-plus ml-[0.5rem]" />
              </button>
              <button
                className="text-gray-400 hover:text-red-500 text-xs invisible group-hover:visible"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteNode(data.id);
                }}
                title="Delete node"
              >
                <i className="pi pi-trash ml-[0.5rem]" />
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="custom-tree-wrapper bg-[#000000] flex flex-col h-full">
      <ConfirmDialog />
      <div className="mt-2 mx-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-white font-semibold text-sm">
          <EditNoteRoundedIcon fontSize="small" />
          <span>Managebook</span>
        </div>
        <div className="relative w-full max-w-[150px] sm:max-w-[200px] md:max-w-[250px]" ref={dropdownRef}>
          <button
            onClick={() => setShowGenreDropdown(!showGenreDropdown)}
            className="flex items-center justify-between w-full bg-gradient-to-r from-blue-600 to-blue-800 text-white text-xs px-3 py-2 rounded-md hover:from-blue-500 hover:to-blue-700 transition-all duration-200 shadow-sm"
          >
            <span className="mr-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </span>
            <span className="truncate">Add Genre</span>
            <span className="ml-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </span>
          </button>
          {showGenreDropdown && (
            <div className="absolute z-10 w-full mt-1">
              <Dropdown
                options={genreOptions}
                onChange={(e) => handleAddParentNode(e.value)}
                placeholder="Select Genre"
                className="text-xs w-full bg-white rounded-md shadow-lg"
                panelClassName="text-xs"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                autoFocus
              />
            </div>
          )}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        <Tree
          value={treeNodes}
          selectionMode="single"
          onSelect={handleSelection}
          nodeTemplate={nodeTemplate}
          dragdropScope="ebookTree"
          draggable
          droppable
          onDragDrop={onDragDrop}
          className="custom-tree"
          style={{ backgroundColor: '#000000' }}
        />
      </div>
    </div>
  );
}

const getIconByGenre = (genreName) => {
  const iconProps = { sx: { color: '#ffffff' }, fontSize: 'small' };
  switch (genreName?.toLowerCase()) {
    case 'chapter':
      return <MenuBookRoundedIcon {...iconProps} />;
    case 'module':
      return <ViewModuleRoundedIcon {...iconProps} />;
    case 'rule':
      return <GavelRoundedIcon {...iconProps} />;
    case 'sub-rule':
      return <DocumentScannerRoundedIcon {...iconProps} />;
    case 'section':
      return <AccountTreeRoundedIcon {...iconProps} />;
    case 'object & reason':
      return <EmojiObjectsRoundedIcon {...iconProps} />;
    case 'case law':
      return <BalanceRoundedIcon {...iconProps} />;
    case 'schedule':
      return <ViewDayRoundedIcon {...iconProps} />;
    case 'omitted':
      return <BlockRoundedIcon {...iconProps} />;
    default:
      return 'pi pi-file';
  }
};

const transformToTreeNodes = (data) => {
  return data.map((item) => ({
    key: item.id.toString(),
    label: item.title,
    data: item,
    icon: getIconByGenre(item.genre?.name),
    children: transformToTreeNodes(item.children || []),
    leaf: !(item.children && item.children.length > 0),
  }));
};